import type { DefaultTheme } from "styled-components";

// theme.ts — add near the top, before the theme object
export function alpha(hex: string, opacity: number): string {
  const clean = hex.replace("#", "").slice(0, 6);
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const theme: DefaultTheme = {
  typography: {
    fonts: {
      thin: "'FiraSans', sans-serif",
      extralight: "'FiraSans', sans-serif",
      light: "'FiraSans', sans-serif",
      regular: "'FiraSans', sans-serif",
      medium: "'FiraSans', sans-serif",
      semibold: "'FiraSans', sans-serif",
      bold: "'FiraSans', sans-serif",
      extrabold: "'FiraSans', sans-serif",
      black: "'FiraSans', sans-serif",

      thinItalic: "'FiraSans', sans-serif",
      extralightItalic: "'FiraSans', sans-serif",
      lightItalic: "'FiraSans', sans-serif",
      regularItalic: "'FiraSans', sans-serif",
      mediumItalic: "'FiraSans', sans-serif",
      semiboldItalic: "'FiraSans', sans-serif",
      boldItalic: "'FiraSans', sans-serif",
      extraboldItalic: "'FiraSans', sans-serif",
      blackItalic: "'FiraSans', sans-serif",

      numbers: "'RobotoFlex-VariableFont', sans-serif",
      mono: "'Fira Mono', 'JetBrains Mono', monospace",
    },
    sizes: {
      header: "2rem",
      body: "0.8rem",
      label: "16px",
      title: "1.5rem",
      helper: "0.7rem",
      hint: "0.75rem",
      tab: "0.6rem",
      subject: "1rem",
      xl: "2.5rem",
    },
    weights: {
      header: 900,
      body: 500,
      title: 600,
      subject: 500,
      light: 350,
    },
    lineHeights: {
      body: "1.5",
      heading: "1.2",
    },
    letterSpacing: {
      body: "0.02em",
      heading: "0.01em",
    },
  },

  colors: {
    primary: "#ea7f05",
    primarySoft: "#ea7f050e",
    error: "#ff471e",
    success: "#9b9e00",
    warning: "#c2790a",
    info: "#002968",

    accent: "#002968",
    textPrimary: "#272525",
    textSecondary: "#514f4f",
    primaryBackground: "#f8eee2",
    secondaryBackground: "#f2e0cb",
    brand: {
      green: "#9b9e00ff",
      red: "#ff471e",
      blue: "#002968",
      white: "#f7f7f7",
    },
    disabled: "#1e2021",
    semitransparent: "#f8eee283",
    static: {
      black: "#272525",
      grey: "#868686b7",
      white: "#f8eee2",
    },
  },
  gradients: {
    primary: "linear-gradient(135deg, #ea7f05 0%, #b96300 100%)",
    strong: "linear-gradient(135deg, #ffaa5b, #ffa33a)",
    secondary: "linear-gradient(135deg, #e5c7a6, rgb(231, 165, 89))",
    glass: "linear-gradient(135deg, #f2e0cb, rgba(226, 214, 199, 0.36))",
  },
  spacing: {
    tight: "0.2rem",
    small: "0.5rem",
    medium: "1rem",
    large: "2rem",
    xlarge: "4rem",
  },
  borders: {
    width: {
      thin: "1px",
      medium: "2px",
      thick: "4px",
    },
    radius: {
      small: "4px",
      medium: "8px",
      big: "12px",
      large: "16px",
      rounded: "50px",
    },
    color: {
      primary: "#ea7f05",
      secondary: "#1e2021",
    },
  },
  shadows: {
    small: "0 1px 3px rgba(0, 0, 0, 0.12)",
    medium: "0 4px 6px rgba(0, 0, 0, 0.16)",
    large: "0 10px 20px rgba(0, 0, 0, 0.24)",
  },
  breakpoints: {
    mobile: "480px",
    tablet: "768px",
    laptop: "1024px",
    desktop: "1200px",
  },
  zIndex: {
    subroot: -500,
    root: 0,
    suboverlay: 900,
    overlay: 1000,
    overlayContent: 1500,
    modal: 2000,
    dropdown: 500,
    tooltip: 200,
    topmost: 10000,
    consent: 20000,
  },
  animations: {
    duration: {
      short: "200ms",
      medium: "400ms",
      long: "600ms",
    },
    easing: {
      easeIn: "cubic-bezier(0.5, 0, 0.5, 1)",
      easeOut: "cubic-bezier(0, 0, 0.5, 1)",
      easeInOut: "cubic-bezier(0.5, 0, 0.5, 1)",
    },
  },
  iconSizes: {
    small: "16px",
    medium: "24px",
    large: "32px",
    xlarge: "48px",
  },
};

export default theme;
